import {
  Card,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { format } from 'date-fns';
import { StatusPill } from './npc-status';

const cardSx = {
  borderRadius: '20px',
  boxShadow: '0px 5px 22px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.06)',
  overflow: 'hidden',
};
const headCellSx = {
  textTransform: 'uppercase',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.5px',
  color: '#2F3746',
  bgcolor: '#F8F9FA',
  whiteSpace: 'nowrap',
};

const fmtDate = (d: string) => {
  if (!d) return '—';
  // d is 'yyyy-MM-dd' — parse as local midnight to avoid tz off-by-one.
  return format(new Date(`${d}T00:00:00`), 'MMM d, yyyy');
};

const NpcLogTable = ({ list, onEdit, onDelete }: any) => {
  const loading = list?.loading;
  // THIN-8233: guard against a non-array `response` (a failed fetch used to store the raw error
  // here) so .map below can't throw and blank the page behind the route ErrorBoundary.
  const entries: any[] = Array.isArray(list?.response) ? list.response : [];

  if (loading) {
    return (
      <Card sx={{ ...cardSx, p: 2 }}>
        {[...Array(4)].map((_, i) => (
          <Skeleton
            key={i}
            height={52}
          />
        ))}
      </Card>
    );
  }

  return (
    <Card sx={cardSx}>
      <Table sx={{ '& td': { py: 1.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Therapist</TableCell>
            <TableCell sx={headCellSx}>Date</TableCell>
            <TableCell sx={headCellSx}>Activity</TableCell>
            <TableCell sx={headCellSx}>Patient</TableCell>
            <TableCell sx={headCellSx}>Duration</TableCell>
            <TableCell sx={headCellSx}>Comment</TableCell>
            <TableCell sx={headCellSx}>Status</TableCell>
            <TableCell
              align="right"
              sx={headCellSx}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries?.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography
                  color="text.secondary"
                  align="center"
                  sx={{ py: 4 }}
                >
                  No NPC entries for this period.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {entries?.map((e) => (
            <TableRow
              key={e.uuid}
              hover
            >
              <TableCell>{e.therapistName || '—'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</TableCell>
              <TableCell>{e.activityTypeLabel}</TableCell>
              <TableCell>{e.patientName || '—'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.durationMinutes} min</TableCell>
              <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', color: '#6C737F' }}>
                {e.comment || '—'}
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <StatusPill status={e.status} />
                  {e.status === 'DECLINED' && e.declineReason && (
                    <Tooltip title={e.declineReason}>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                      >
                        reason
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => onEdit(e)}
                  aria-label="edit"
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(e)}
                  aria-label="delete"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default NpcLogTable;
